import React, { useMemo, useState } from 'react';
import {
  validateActivityPayload,
  GAME_MODE_CATALOG,
  SequencerTimeline,
  resolveSequencerPresentation,
  type SequencerPayload,
} from '@interactive-room';
import { emptySequencerPayload } from '../emptyPayloads';
import { StudioLiveShell } from '../components/StudioLiveShell';
import { SequencerStepsCollapsible } from '../components/SequencerStepsCollapsible';
import { createDraftId, saveActivityDraft, type ActivityDraft } from '../activityStorage';
import { resolveStudioLaunchDefaults } from '../studioLaunch';
import { generateSubjectAwareActivityDraft } from '../studioActivityGeneration';
import type { StudioLiveEditorProps } from '../types/editor.types';

export function SequencerLiveEditor({
  draftId,
  embedded,
  onSaved,
  launchSubjectId,
  launchPresetId,
}: StudioLiveEditorProps) {
  const launch = resolveStudioLaunchDefaults(emptySequencerPayload, {
    draftId,
    launchSubjectId,
    launchPresetId,
  });
  const [payload, setPayload] = useState<SequencerPayload>(() => launch.payload);
  const [subjectTag, setSubjectTag] = useState(() => launch.subjectTag);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [savedId] = useState(draftId ?? createDraftId());
  const [expandedStep, setExpandedStep] = useState(0);

  const meta = GAME_MODE_CATALOG.order;
  const presentation = resolveSequencerPresentation(subjectTag);
  const order = useMemo(
    () => (payload.correctOrder.length ? payload.correctOrder : payload.items.map((_, i) => i)),
    [payload.correctOrder, payload.items],
  );

  const updateItem = (index: number, text: string) => {
    setPayload((prev) => {
      const items = [...prev.items];
      items[index] = text;
      return { ...prev, items, correctOrder: items.map((_, i) => i) };
    });
  };

  const addItem = () => {
    setPayload((prev) => {
      const items = [...prev.items, `Paso ${prev.items.length + 1}`];
      return { ...prev, items, correctOrder: items.map((_, i) => i) };
    });
    setExpandedStep(payload.items.length);
  };

  const removeItem = (index: number) => {
    setPayload((prev) => {
      const items = prev.items.filter((_, i) => i !== index);
      return { ...prev, items, correctOrder: items.map((_, i) => i) };
    });
    setExpandedStep((prev) => Math.max(0, prev >= index ? prev - 1 : prev));
  };

  const handleAiBuild = async (prompt: string) => {
    setAiLoading(true);
    setError(null);
    setNotice(null);
    try {
      const { draft, notice: aiNotice } = await generateSubjectAwareActivityDraft({
        mode: 'order',
        subjectId: launchSubjectId,
        subjectTag: subjectTag.trim() || undefined,
        tuning: prompt,
        draftId: savedId,
        persist: false,
      });
      setPayload(draft.payload as SequencerPayload);
      if (draft.subjectTag) setSubjectTag(draft.subjectTag);
      setExpandedStep(0);
      if (aiNotice) setNotice(aiNotice);
    } catch (e) {
      setError(
        e instanceof Error && e.message === 'subject_context_required'
          ? 'Selecciona una asignatura desde el Studio para generar con IA.'
          : e instanceof Error
            ? e.message
            : 'No se pudo generar con IA',
      );
    } finally {
      setAiLoading(false);
    }
  };

  const save = () => {
    try {
      validateActivityPayload('order', payload);
      setError(null);
      onSaved?.(
        saveActivityDraft({
          id: savedId,
          mode: 'order',
          title: payload.title,
          subjectTag: subjectTag.trim() || 'general',
          payload,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
    }
  };

  const settingsContent = (
    <div className="space-y-4">
      <label className="block">
        <span className="sala-label">Asignatura / etiqueta</span>
        <input
          className="sala-input-dark mt-2 w-full text-sm"
          value={subjectTag}
          onChange={(e) => setSubjectTag(e.target.value)}
          placeholder="general, historia, música…"
        />
      </label>
      <label className="block">
        <span className="sala-label">Título interno</span>
        <input
          className="sala-input-dark mt-2 w-full text-sm"
          value={payload.title}
          onChange={(e) => setPayload((p) => ({ ...p, title: e.target.value }))}
          placeholder="Título de la actividad"
        />
      </label>
      <p className="text-[10px] leading-relaxed text-slate-400">
        Los parámetros avanzados (temporizadores, puntuación) vivirán aquí cuando el motor los exponga.
      </p>
    </div>
  );

  return (
    <StudioLiveShell
      embedded={embedded}
      layout={embedded ? 'split' : 'default'}
      error={error}
      onSave={save}
      settingsContent={settingsContent}
      settingsTitle="Ajustes · Secuencias"
      aiActivityLabel="secuencia"
      onAiBuild={handleAiBuild}
      aiLoading={aiLoading}
    >
      {notice && (
        <p className="mx-4 mt-2 rounded-lg border border-amber-500/30 bg-amber-950/20 px-3 py-2 text-[10px] text-amber-200/90">
          {notice}
        </p>
      )}
      {aiLoading && (
        <p className="mx-4 mt-2 text-center text-xs text-[var(--plugin-muted)]">Generando secuencia…</p>
      )}
      <header className="plugin-live-toolbar plugin-live-toolbar--ghost">
        <input
          className="canvas-inline-input canvas-inline-input--title min-w-0 flex-1 bg-transparent font-display text-base font-semibold tracking-tight"
          value={payload.title}
          onChange={(e) => setPayload((p) => ({ ...p, title: e.target.value }))}
          placeholder="Título"
        />
        <span className="studio-pill">{meta.displayName}</span>
      </header>

      <div className="studio-scaffold flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-4">
        <section className="studio-zone studio-zone--prompt shrink-0">
          <p className="studio-zone__label">Consigna</p>
          <textarea
            className="canvas-inline-input mt-2 w-full resize-none bg-transparent text-sm font-medium text-slate-600 dark:text-slate-300"
            rows={2}
            value={payload.prompt}
            placeholder="Instrucción para la clase…"
            onChange={(e) => setPayload((p) => ({ ...p, prompt: e.target.value }))}
          />
        </section>

        <section className="studio-zone studio-zone--preview flex min-h-0 flex-1 flex-col">
          <p className="studio-zone__label">Vista previa del flujo</p>
          <div className="studio-zone__canvas mt-2 min-h-0 flex-1 overflow-auto">
            <SequencerTimeline items={payload.items} order={order} presentation={presentation} />
          </div>
        </section>

        <section className="studio-zone studio-zone--steps shrink-0">
          <SequencerStepsCollapsible
            items={payload.items}
            expandedIndex={expandedStep}
            onExpandedChange={setExpandedStep}
            onItemChange={updateItem}
            onAdd={addItem}
            onRemove={removeItem}
          />
        </section>
      </div>
    </StudioLiveShell>
  );
}
